<script setup lang="ts">
    import { InputRow, MultiCombobox, DatePicker, Select, Combobox } from '@/components/ui/form';
    import { Textarea } from '@/components/ui/textarea';
    import { Input } from '@/components/ui/input';
    import { optionsDays, optionsYesNo } from '@/utils/form';
    import { computed } from 'vue';
    import { useI18n } from 'vue-i18n';
    import type { SelectOption } from '@/types';
    import { useTaskResponsiblesData } from '@/composables/useTaskResponsiblesData';
    import { useTaskVoucherData } from '@/composables/useTaskVoucherData';
    import FileView from '@/components/FileView.vue';
    import axios from 'axios';
    import { useQueryClient } from '@tanstack/vue-query';
    import QueryKey from '@/enums/QueryKey';
    import { usePage } from '@inertiajs/vue3';
    import type { SharedData } from '@/types';

    const props = defineProps<{
        form: any;
    }>();

    const queryClient = useQueryClient();

    const { taskResponsibles, isTaskResponsiblesPending } = useTaskResponsiblesData();
    const { taskVouchers, isTaskVouchersPending } = useTaskVoucherData(props.form.task_id);

    const page = usePage<SharedData>();
    const isClient = !!page.props.auth.user.is_client;

    const { t, tm } = useI18n();

    const statusOptions = computed<SelectOption[]>(() => {
        const map = tm('task-templates.statuses') as Record<string, string>;
        return Object.entries(map).map(([value, label]) => ({ value, label }));
    });

    const frequencyOptions = computed<SelectOption[]>(() => {
        const map = tm('task-templates.frequency_options') as Record<string, string>;
        return Object.entries(map).map(([value, label]) => ({ value, label }));
    });

    const weekOptions = computed<SelectOption[]>(() => {
        const map = tm('task-templates.odd_or_even_week_options') as Record<string, string>;
        return Object.entries(map).map(([value, label]) => ({ value, label }));
    });

    const monthOptions = computed<SelectOption[]>(() => {
        const map = tm('task-templates.start_or_end_month_options') as Record<string, string>;
        return Object.entries(map).map(([value, label]) => ({ value, label }));
    });

    const responsibleOptions = computed<SelectOption[]>(() => {
        const { responsibleCompanyUsers = {}, responsibleEmployeesKeepers = {} } =
            taskResponsibles.value || {};

        const hasCompanies = !!Object.keys(responsibleCompanyUsers).length;
        const hasEmployees = !!Object.keys(responsibleEmployeesKeepers).length;

        const makeGroup = (labelKey: string, entries: Record<string, unknown>) => [
            { is_group: true, label: t(labelKey), value: '' },
            ...Object.entries(entries).map(([value, label]) => ({ value, label: label as string })),
        ];

        const options: SelectOption[] = [];
        if (hasEmployees) options.push(...makeGroup('menu.employees', responsibleEmployeesKeepers));
        if (hasCompanies && hasEmployees)
            options.push({ is_seperator: true, value: '', label: '' });
        if (hasCompanies) options.push(...makeGroup('companies.clients', responsibleCompanyUsers));

        return options;
    });

    const files = computed(() => {
        return taskVouchers.value.map((file: any) => ({
            name: file.filename,
            url: `/uploads/${file.filename}`,
            id: file.task_voucher_id,
        }));
    });

    const handleDeleteFile = (file: any) => {
        axios.delete(route('v2.dashboard.tasks.vouchers.delete', file.id)).then((response) => {
            queryClient.invalidateQueries({
                queryKey: [QueryKey.DASHBOARD_TASKS_VOUCHERS, props.form.task_id],
            });
        });
    };
</script>

<template>
    <div class="flex flex-col gap-3">
        <InputRow
            :form="form"
            name="task_title"
            label="dashboards.tasks_title"
        >
            <Input
                id="task_title"
                v-model="form.task_title"
                autocomplete="task_title"
                :placeholder="$t('dashboards.tasks_title')"
                :disabled="isClient"
            />
        </InputRow>

        <InputRow
            :form="form"
            name="task_description"
            label="dashboards.tasks_description"
        >
            <Textarea
                id="task_description"
                v-model="form.task_description"
                :placeholder="$t('dashboards.tasks_description')"
                :disabled="isClient"
            />
        </InputRow>
        <InputRow
            :form="form"
            name="deadline"
            label="dashboards.tasks_deadline"
        >
            <DatePicker
                v-model="form.deadline"
                :disabled="isClient"
            />
        </InputRow>
        <InputRow
            :form="form"
            name="weekdays"
            label="dashboards.notification.day_in_week"
        >
            <MultiCombobox
                v-model="form.weekdays"
                :options="optionsDays"
                translate
                :placeholder="$t('dashboards.notification.day_in_week')"
                :disabled="isClient"
            />
        </InputRow>
        <InputRow
            :form="form"
            name="filename"
            label="task-templates.attach"
        >
            <Input
                id="filename"
                :placeholder="$t('task-templates.attach')"
                type="file"
                @input="form.filename = $event.target.files[0]"
                :disabled="isClient"
            />
        </InputRow>

        <div
            v-if="files.length > 0"
            class="flex w-full flex-col gap-2"
        >
            <FileView
                v-for="file in files"
                :key="file.id"
                :file="file"
                :previewable="false"
                @delete="handleDeleteFile"
            />
        </div>

        <InputRow
            :form="form"
            name="task_status"
            label="task-templates.task.status"
        >
            <Select
                v-model="form.task_status"
                :options="statusOptions"
                :disabled="isClient"
            />
        </InputRow>

        <div class="border-secondary flex flex-col gap-3 rounded-md border p-2.5">
            <InputRow
                :form="form"
                name="send_notification"
                label="task-templates.task.send_notification"
            >
                <Select
                    v-model="form.send_notification"
                    :options="optionsYesNo"
                    translate
                    :disabled="isClient"
                />
            </InputRow>
            <InputRow
                :form="form"
                name="notification_recurring_week"
                label="dashboards.notification.frequency"
            >
                <Select
                    v-model="form.notification_recurring_week"
                    :options="frequencyOptions"
                    :disabled="isClient"
                />
            </InputRow>

            <InputRow
                v-if="form.notification_recurring_week === '2'"
                :form="form"
                name="notification_recurring_even_week"
                label="dashboards.notification.choose_odd_or_even_week"
            >
                <Select
                    v-model="form.notification_recurring_even_week"
                    :options="weekOptions"
                    :disabled="isClient"
                />
            </InputRow>
            <InputRow
                v-if="form.notification_recurring_week === '4'"
                :form="form"
                name="notification_recurring_start_of_month"
                label="dashboards.notification.choose_start_or_end_month"
            >
                <Select
                    v-model="form.notification_recurring_start_of_month"
                    :options="monthOptions"
                    :disabled="isClient"
                />
            </InputRow>

            <InputRow
                :form="form"
                name="responsible_ids"
                label="task-templates.responsible"
            >
                <Combobox
                    v-model="form.responsible_ids"
                    :options="responsibleOptions"
                    :show-remove-button="false"
                    :loading="isTaskResponsiblesPending"
                    :disabled="isClient"
                    side="top"
                />
            </InputRow>
        </div>
    </div>
</template>
